/** @module What the collection screen asks the server for; kept apart so it can be tested. */

export { CONDITIONS, LANGUAGES } from "@/lib/collection";
/** Rarity classes of the catalogue, from the rarest down. */
export const RARITIES = ["illustration", "secret", "full_art", "holo_rare", "standard"] as const;

export interface Filters {
  /** Free text: a card name or an id like "sv09-185". */
  query: string;
  status: "active" | "archived";
  language: string | null;
  condition: string | null;
  rarity: string | null;
  /** Tag ids; a copy has to carry all of them. */
  tags: string[];
  /** Copies with or without a purchase price, to fill the gaps in one go. */
  purchase: "with" | "without" | null;
  /** A place id (compartments included) or "none" for copies without a place. */
  location: string | null;
}

export const EMPTY: Filters = {
  query: "",
  status: "active",
  language: null,
  condition: null,
  rarity: null,
  tags: [],
  purchase: null,
  location: null,
};

/**
 * The filters as query parameters, for the list, the export and the print list.
 *
 * Empty values are left out rather than sent as null: the server treats a missing
 * parameter as "no filter", and an explicit null would mean something else.
 */
export function filterParams(filters: Filters): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = { status: filters.status };
  const trimmed = filters.query.trim();
  if (trimmed) params.q = trimmed;
  if (filters.language) params.language = filters.language;
  if (filters.condition) params.condition = filters.condition;
  if (filters.rarity) params.rarity_class = filters.rarity;
  if (filters.tags.length) params.tag = filters.tags;
  if (filters.purchase) params.purchase = filters.purchase;
  if (filters.location) params.location = filters.location;
  return params;
}

/** One page of the list: the filters plus page size and cursor. */
export function toQuery(
  filters: Filters,
  cursor?: string | null,
): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = { ...filterParams(filters), limit: "60" };
  if (cursor) params.cursor = cursor;
  return params;
}

/** The filters in an address (the print list), and back. */
export interface FilterSearch {
  status?: "archived";
  q?: string;
  language?: string;
  condition?: string;
  rarity?: string;
  tag?: string[];
  purchase?: "with" | "without";
  location?: string;
}

export function toSearch(filters: Filters): FilterSearch {
  const search: FilterSearch = {};
  if (filters.status === "archived") search.status = "archived";
  if (filters.query.trim()) search.q = filters.query.trim();
  if (filters.language) search.language = filters.language;
  if (filters.condition) search.condition = filters.condition;
  if (filters.rarity) search.rarity = filters.rarity;
  if (filters.tags.length) search.tag = filters.tags;
  if (filters.purchase) search.purchase = filters.purchase;
  if (filters.location) search.location = filters.location;
  return search;
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function fromSearch(search: Record<string, unknown>): FilterSearch {
  const tags = Array.isArray(search.tag)
    ? search.tag.filter((tag): tag is string => typeof tag === "string")
    : text(search.tag)
      ? [String(search.tag)]
      : [];
  const result: FilterSearch = {};
  if (search.status === "archived") result.status = "archived";
  if (text(search.q)) result.q = text(search.q);
  if (text(search.language)) result.language = text(search.language);
  if (text(search.condition)) result.condition = text(search.condition);
  if (text(search.rarity)) result.rarity = text(search.rarity);
  if (tags.length) result.tag = tags;
  if (search.purchase === "with" || search.purchase === "without")
    result.purchase = search.purchase;
  if (text(search.location)) result.location = text(search.location);
  return result;
}

export function filtersOf(search: FilterSearch): Filters {
  return {
    query: search.q ?? "",
    status: search.status ?? "active",
    language: search.language ?? null,
    condition: search.condition ?? null,
    rarity: search.rarity ?? null,
    tags: search.tag ?? [],
    purchase: search.purchase ?? null,
    location: search.location ?? null,
  };
}

/** How many filters are set; the screen shows the number on the filter button. */
export function activeCount(filters: Filters): number {
  return [
    filters.query.trim() !== "",
    filters.status !== EMPTY.status,
    filters.language !== null,
    filters.condition !== null,
    filters.rarity !== null,
    filters.tags.length > 0,
    filters.purchase !== null,
    filters.location !== null,
  ].filter(Boolean).length;
}
